// Centralised navigation config — single source of truth for the consolidated
// sidebar (8 top-level items) and the per-group sub-tab pill bar.
import {
  LayoutDashboard, Building2, Users, FlaskConical, Brain, Radar, Sparkles,
  Megaphone, Database, Layers, UserCircle, TrendingUp, Mail, ShieldAlert,
  Globe, MessageSquare, Inbox, Briefcase, Truck, PlayCircle, GraduationCap,
  Syringe, Undo2, Stethoscope, AlertCircle, Handshake, RefreshCw, ShieldCheck,
  FileText, BarChart3, Landmark,
} from 'lucide-react';

// Top-level sidebar items. `to` is the default landing route for the group.
// `match` is the list of path prefixes that should highlight this item.
export const NAV_GROUPS = [
  {
    key: 'dashboard',
    to: '/dashboard',
    icon: LayoutDashboard,
    label: 'Dashboard',
    match: ['/dashboard'],
  },
  {
    key: 'alerts',
    to: '/alerts',
    icon: Radar,
    label: 'Alerts',
    match: ['/alerts', '/signals'],
    showBadge: true,
  },
  {
    key: 'biotechs',
    to: '/biotech',
    icon: Building2,
    // Renamed 2026-02 per user's reframing: "biotechs" is too narrow — the
    // sidebar now reflects the BD intent (out-licensers = anyone who sells
    // assets, regardless of whether they're a classical biotech, NDDS
    // platform, DTx, university spinout, etc.). Internal URLs stay
    // `/biotech*` for backward-compat with existing bookmarks and email
    // links. Page titles still say "Biotech Companies" where appropriate.
    label: 'Out-Licensers',
    match: ['/biotech', '/biotech-contacts'],
  },
  {
    key: 'assets',
    to: '/assets',
    icon: FlaskConical,
    label: 'Assets',
    // Specialty/academic/NDDS/DTX routes still navigate to the
    // standalone SpecialtyAssetsPage (bookmarks + email-link compatibility),
    // but no separate sidebar entry — keep everything under Assets.
    match: ['/assets'],
  },
  {
    key: 'in_licensers',
    to: '/company-profiles',
    icon: Users,
    // Unified In-Licensers entry (2026-02): collapses the previously-
    // separate "Pharma" and "Distributors" top-level items into ONE,
    // since both are buyers from your perspective. The phase-gating
    // (distributors only for Phase 3+) lives in AI Mapping, not in the
    // navigation. Sub-tabs Companies/Contacts unify the two underlying
    // collections (company_profiles category=pharma vs regional_distributor).
    label: 'In-Licensers',
    match: ['/company-profiles', '/company-profile', '/pharma', '/regional-distributors', '/in-licensers'],
  },
  {
    key: 'accounts',
    to: '/accounts',
    icon: Briefcase,
    label: 'Accounts',
    match: ['/accounts'],
  },
  {
    key: 'ai',
    to: '/ai-mapping',
    icon: Sparkles,
    label: 'AI Studio',
    match: ['/intelligence-hub', '/ai-mapping', '/copilot', '/opportunity-lists', '/portfolio-match'],
  },
  {
    key: 'clients',
    to: '/clients',
    icon: Briefcase,
    label: 'Clients',
    match: ['/abacus', '/clients', '/engagements'],
  },
  {
    key: 'investors',
    to: '/investors',
    icon: Landmark,
    label: 'Investors',
    match: ['/investors'],
  },
  {
    // Jul 2026 — Animal Health Repositioning module (isolated from Viatris).
    // Its own workspace for defining screening rules that translate broad
    // TA + modality + stage intent into structured filters, saved as
    // reusable templates. Global human pharma/biotech universe.
    key: 'animal_health',
    to: '/animal-health',
    icon: Stethoscope,
    label: 'Animal Health',
    match: ['/animal-health', '/animal-health/cohort', '/animal-health/browse'],
  },
  {
    key: 'outreach',
    to: '/campaigns',
    icon: Megaphone,
    label: 'Outreach',
    match: ['/campaigns', '/met-in-person', '/follow-up-composer', '/outreach', '/follow-ups',
            '/email-tracking', '/outreach-status', '/weekly-digest'],
  },
  {
    key: 'ops',
    to: '/imports',
    icon: Database,
    label: 'Data Ops',
    match: ['/imports', '/audit', '/duplicates', '/merge-recovery', '/db-sync', '/contact-matching', '/email-coverage', '/admin/pharma-cleanup', '/admin/recover-distributors', '/admin/verify-classifications', '/asset-imports', '/data-ops/ta-backfill', '/data-ops/ta-audit', '/data-ops/roa-backfill', '/data-ops/country-backfill', '/data-ops/stage-backfill', '/data-ops/stage-report', '/data-ops/modality-backfill', '/data-ops/indication-backfill', '/data-ops/asset-standardization', '/data-ops/handshake-classifier', '/data-ops/deep-research-backfill', '/data-ops/rerun-stale-mappings', '/data-ops/review-queue', '/data-ops/exclusion-audit', '/data-ops/deck-reenrich', '/data-ops/suppressed-contacts', '/data-ops/hidden-emails', '/data-ops/viatris-duplicates'],
  },
  {
    key: 'enquiries',
    to: '/data-ops/enquiries',
    icon: Inbox,
    label: 'Web Enquiries',
    match: ['/data-ops/enquiries'],
  },
  {
    key: 'demo',
    to: '/demo',
    icon: PlayCircle,
    label: 'Demo',
    match: ['/demo'],
  },
  {
    key: 'analytics',
    to: '/admin/analytics',
    icon: BarChart3,
    label: 'Analytics',
    match: ['/admin/analytics'],
    adminOnly: true,
  },
];

// Sub-tab pill bars rendered at the top of the main content area when the
// current route belongs to a multi-page group.
export const SUB_TABS = {
  biotechs: [
    // Jul 2026 · Out-licensing scope: the Formulation/Academic/DTx/Platform
    // specialty tabs were removed — out-licensing operates only on
    // therapeutic biotechs. Specialty data remains in the DB, unreachable.
    { to: '/biotech', label: 'Biotech Companies', icon: Building2,
      match: ['/biotech'], notMatch: ['/biotech-contacts'] },
    { to: '/biotech-contacts', label: 'Contacts', icon: UserCircle,
      match: ['/biotech-contacts'] },
  ],
  assets: [
    { to: '/assets', label: 'Pipeline · All', icon: FlaskConical,
      match: ['/assets'],
      notMatch: [] },
    // Off-Patent · Tail sub-tab removed Jul 2026 — tail assets purged from scope.
  ],
  pharma: [
    // Legacy key retained for activeGroupKey resolution on old /pharma URLs.
    { to: '/company-profiles', label: 'Companies', icon: Building2,
      match: ['/company-profiles', '/company-profile'] },
    { to: '/pharma', label: 'Contacts', icon: UserCircle,
      match: ['/pharma'] },
  ],
  // Unified In-Licensers sub-tab bar — three category lenses + a Contacts tab.
  // The Companies tabs all route to /company-profiles with a `?category=` query
  // param so the same page can render filtered lists without code duplication.
  in_licensers: [
    { to: '/company-profiles', label: 'All', icon: Building2,
      match: ['/company-profiles', '/company-profile', '/in-licensers'],
      notMatch: ['/regional-distributors', '/pharma'] },
    { to: '/company-profiles?category=pharma', label: 'Pharma', icon: Users,
      match: ['/company-profiles?category=pharma'] },
    { to: '/regional-distributors', label: 'Commercial Partners', icon: Truck,
      match: ['/regional-distributors'] },
    { to: '/pharma', label: 'Contacts', icon: UserCircle,
      match: ['/pharma'] },
  ],
  regional_distributors: [
    { to: '/regional-distributors', label: 'Companies', icon: Building2,
      match: ['/regional-distributors'] },
  ],
  ai: [
    { to: '/ai-mapping', label: 'AI Mapping', icon: Brain,
      match: ['/ai-mapping'] },
    { to: '/portfolio-match', label: 'AI Portfolio Match', icon: Sparkles,
      match: ['/portfolio-match'] },
    { to: '/intelligence-hub', label: 'Asset Evaluator', icon: Sparkles,
      match: ['/intelligence-hub'] },
    { to: '/copilot', label: 'Research Copilot', icon: MessageSquare,
      match: ['/copilot', '/opportunity-lists'] },
  ],
  clients: [
    // 2026-02: surface Viatris workbench as a sub-tab under Clients.
    // Other client engagements (Abacus etc.) live on the main /clients
    // list page until they get their own workbench experiences.
    // Jul 2026: Modulus sub-tab removed — animal-health work now lives
    // in the dedicated top-level "Animal Health" nav item.
    { to: '/clients', label: 'All Clients', icon: Briefcase,
      match: ['/clients', '/engagements'], notMatch: ['/clients/viatris'] },
    { to: '/clients/viatris', label: 'Viatris', icon: Sparkles,
      match: ['/clients/viatris'] },
  ],
  animal_health: [
    // Jul 2026 — dedicated tab for the Animal Health Repositioning module.
    // Isolated from Viatris; runs against the global human pharma/biotech
    // asset universe (no separate vet-only universe).
    { to: '/animal-health', label: 'Screening Rules', icon: Stethoscope,
      match: ['/animal-health'] },
    { to: '/animal-health/cohort', label: 'Eligible Cohort', icon: Sparkles,
      match: ['/animal-health/cohort'] },
    { to: '/animal-health/browse', label: 'Browse by Disease', icon: Sparkles,
      match: ['/animal-health/browse'] },
  ],
  outreach: [
    { to: '/campaigns', label: 'Campaigns', icon: Megaphone,
      match: ['/campaigns'] },
    { to: '/met-in-person', label: 'Met In-Person', icon: UserCircle,
      match: ['/met-in-person'] },
    { to: '/weekly-digest', label: 'Weekly Digest', icon: Mail,
      match: ['/weekly-digest'] },
  ],
  ops: [
    { to: '/imports', label: 'Conference Imports', icon: Database,
      match: ['/imports'] },
    { to: '/asset-imports', label: 'Asset Imports', icon: Database,
      match: ['/asset-imports'] },
    { to: '/data-ops/asset-standardization', label: 'Asset Standardization', icon: Sparkles,
      match: ['/data-ops/asset-standardization'] },
    { to: '/data-ops/review-queue', label: 'Review Queue', icon: ShieldCheck,
      match: ['/data-ops/review-queue'] },
    { to: '/data-ops/exclusion-audit', label: 'Exclusion Audit', icon: ShieldCheck,
      match: ['/data-ops/exclusion-audit'] },
    { to: '/data-ops/deck-reenrich', label: 'Deck Re-enrich', icon: FileText,
      match: ['/data-ops/deck-reenrich'] },
    { to: '/data-ops/suppressed-contacts', label: 'Suppressed Contacts', icon: ShieldAlert,
      match: ['/data-ops/suppressed-contacts'] },
    { to: '/data-ops/hidden-emails', label: 'Hidden Recipients', icon: ShieldAlert,
      match: ['/data-ops/hidden-emails'] },
    { to: '/data-ops/handshake-classifier', label: 'Handshake Classifier', icon: Handshake,
      match: ['/data-ops/handshake-classifier'] },
    { to: '/data-ops/deep-research-backfill', label: 'Deep Research Backfill', icon: Brain,
      match: ['/data-ops/deep-research-backfill'] },
    { to: '/data-ops/rerun-stale-mappings', label: 'Re-run Stale Mappings', icon: RefreshCw,
      match: ['/data-ops/rerun-stale-mappings'] },
    { to: '/data-ops/ta-backfill', label: 'TA Backfill', icon: Sparkles,
      match: ['/data-ops/ta-backfill'] },
    { to: '/data-ops/ta-audit', label: 'TA Audit', icon: AlertCircle,
      match: ['/data-ops/ta-audit'] },
    { to: '/data-ops/roa-backfill', label: 'RoA Backfill', icon: Syringe,
      match: ['/data-ops/roa-backfill'] },
    { to: '/data-ops/country-backfill', label: 'Country Backfill', icon: Globe,
      match: ['/data-ops/country-backfill'] },
    { to: '/data-ops/stage-backfill', label: 'Phase / Stage Backfill', icon: Layers,
      match: ['/data-ops/stage-backfill'] },
    { to: '/data-ops/stage-report', label: 'Stage Recovery Report', icon: TrendingUp,
      match: ['/data-ops/stage-report'] },
    { to: '/data-ops/scope-purge', label: 'Out-of-Scope Purge', icon: ShieldAlert,
      match: ['/data-ops/scope-purge'] },
    { to: '/data-ops/modality-backfill', label: 'Modality Backfill', icon: FlaskConical,
      match: ['/data-ops/modality-backfill'] },
    { to: '/data-ops/indication-backfill', label: 'Indication Backfill', icon: Stethoscope,
      match: ['/data-ops/indication-backfill'] },
    { to: '/duplicates', label: 'Duplicates', icon: ShieldAlert,
      match: ['/duplicates'] },
    { to: '/merge-recovery', label: 'Merge Recovery', icon: Undo2,
      match: ['/merge-recovery'] },
    { to: '/email-coverage', label: 'Email Coverage', icon: Mail,
      match: ['/email-coverage'] },
    { to: '/audit', label: 'CRM Audit', icon: ShieldAlert,
      match: ['/audit'] },
    { to: '/admin/pharma-cleanup', label: 'Pharma Cleanup', icon: Sparkles,
      match: ['/admin/pharma-cleanup'] },
    { to: '/admin/recover-distributors', label: 'Recover Distributors', icon: Truck,
      match: ['/admin/recover-distributors'] },
    { to: '/admin/verify-classifications', label: 'Verify Classifications', icon: Sparkles,
      match: ['/admin/verify-classifications'] },
    { to: '/db-sync', label: 'Database Sync', icon: Database,
      match: ['/db-sync'], previewOnly: true },
  ],
};

// Matches a pathname against a group's `match` array. Longest prefix wins,
// and the group with the most-specific match is preferred so e.g.
// `/biotech-contacts` resolves to the biotechs group via the contacts entry.
// Query string is stripped from `match` entries before comparison (groups
// only care about the path).
export function activeGroupKey(pathname) {
  let bestKey = null;
  let bestLen = 0;
  for (const g of NAV_GROUPS) {
    for (const m of g.match) {
      const mPath = m.split('?')[0];
      if (pathname === mPath || pathname.startsWith(mPath + '/')) {
        if (mPath.length > bestLen) {
          bestKey = g.key;
          bestLen = mPath.length;
        }
      }
    }
  }
  return bestKey;
}

// Picks the active sub-tab inside a group (longest-match semantics, with
// support for `notMatch` to disambiguate sibling prefixes like
// `/biotech` vs `/biotech-contacts`).
//
// Accepts a `search` (query string) so tabs that differentiate via
// `?category=...` (e.g. In-Licensers' Pharma vs All) activate correctly.
// Match strings may be of the form `/path?key=value` — both pathname AND
// the literal query suffix must equal the URL's pathname+search.
export function activeSubTabIndex(groupKey, pathname, search = '') {
  const tabs = SUB_TABS[groupKey];
  if (!tabs) return -1;
  const fullUrl = pathname + (search || '');
  let bestIdx = -1;
  let bestLen = 0;
  let bestIsQueryAware = false;
  tabs.forEach((tab, idx) => {
    for (const m of tab.match) {
      const isQueryAware = m.includes('?');
      const mPath = m.split('?')[0];
      let isMatch = false;
      if (isQueryAware) {
        // Tab cares about a specific query string — require exact equality
        // with the full pathname+search.
        isMatch = fullUrl === m;
      } else {
        // Plain path match: prefix-based. Tabs WITHOUT a query care about
        // any matching path UNLESS the URL has a query that a sibling
        // query-aware tab handles (handled below by giving query-aware
        // tabs higher precedence when they match).
        isMatch = pathname === mPath || pathname.startsWith(mPath + '/');
      }
      if (!isMatch) continue;
      // notMatch exclusions (path-based, like /biotech excludes /biotech-contacts)
      const blocked = (tab.notMatch || []).some((n) => {
        const nPath = n.split('?')[0];
        return pathname === nPath || pathname.startsWith(nPath + '/');
      });
      if (blocked) continue;
      // Prefer query-aware matches over plain-path matches when both fire.
      const wins = isQueryAware && !bestIsQueryAware ? true
                 : (!isQueryAware && bestIsQueryAware ? false
                 : m.length > bestLen);
      if (wins) {
        bestIdx = idx;
        bestLen = m.length;
        bestIsQueryAware = isQueryAware;
      }
    }
  });
  return bestIdx;
}
